import {inject, Injectable} from "@angular/core";
import { Auth } from 'aws-amplify';
import {AuthService} from "../../services/auth.service";
import { Router, ActivatedRoute, ActivatedRouteSnapshot, RouterStateSnapshot, CanActivate } from '@angular/router';
import {AuthenticatorService} from "@aws-amplify/ui-angular";
import {MatSnackBar} from "@angular/material/snack-bar";
import {AWSLambda} from "../../AWSLambda.service";
import { Observable } from 'rxjs';
import { map, take, filter, switchMap } from 'rxjs/operators';


@Injectable({ providedIn: 'root' })
export class BtbRolesCheckService implements CanActivate {
  snackBar = inject(MatSnackBar);
  lambda = inject(AWSLambda);
  private user = inject(AuthenticatorService).user;
  private auth = inject(AuthService);

  private btbRoutes: string[] = [
    '/btb',
    '/btb/dashboard',
    '/btb/profile',
    '/btb/profile/edit',
  ];

  constructor(
    private authService: AuthService,
    private router: Router
  ) { }

  /**
   * Angular Route Guard - ejecuta ANTES de cargar el componente
   */
  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | boolean {
    // Esperar a que AuthService termine de inicializar (evitar race condition)
    return this.auth.initialized$.pipe(
      filter(initialized => initialized === true),
      take(1),
      switchMap(async () => {
        return await this.checkAccessForUrl(state.url);
      }),
      map(result => result)
    ).toPromise() as Promise<boolean>;
  }

  /**
   * Método manual - para compatibilidad con componentes existentes (DEPRECATED)
   * @deprecated Use CanActivate guard on routes instead
   */
  async checkAccess() {
    const url = this.router.url;
    return await this.checkAccessForUrl(url);
  }

  /**
   * Lógica central de verificación de acceso
   */
  private async checkAccessForUrl(url: string): Promise<boolean> {
    try {
      await this.auth.refresh();

      // Intentar obtener datos del usuario desde Lambda
      let userData;
      try {
        userData = await this.lambda.getLambdaFunction("lambda", "/get/userDetails");
        // console.log('>> btb rolesCheck userData:', userData);
      } catch (lambdaError) {
        console.error('BtbRolesCheck: Error al obtener userDetails desde Lambda:', lambdaError);

        // DECISIÓN DE SEGURIDAD: Bloquear acceso completamente
        // NO usar fallback a localStorage por seguridad
        this.snackBar.open('Error de conexión al verificar permisos. Recarga la página.', 'Close', {
          duration: 5000,
        });
        return false;  // Bloquear acceso - no redirigir
      }

      // console.log('>> btb rolesCheck route:', url);

      // Orden de comprobación:
      // - usuario está habilitado
      // - es superadmin
      // - tiene acceso a rutas específicas del BTB

      if (!this.isEnabled(userData)) {
        this.redirectToHome();
        return false;
      }

      if (this.isSuperAdmin(userData)) {
        return true;
      }

      if (this.matchesRoutePattern(url, this.btbRoutes) && !this.canAccessBTB(userData)) {
        this.redirectToHome();
        return false;
      }

      return true;
    } catch (error) {
      console.error('BtbRolesCheck: Error inesperado en checkAccess:', error);
      // NO redirigir automáticamente - solo bloquear acceso
      this.snackBar.open('Error al verificar permisos. Intenta recargar la página.', 'Close', {
        duration: 5000,
      });
      return false;
    }
  }

  private isEnabled(userData: any) {
    return userData.Enabled === true;
  }

  private isSuperAdmin(userData: any) {
    return userData['custom:backofficeSuperAdmin'] === '1';
  }

  private canAccessBTB(userData: any) {
    return userData['custom:Portal'].includes('btb');
  }

  private redirectToHome() {
    console.warn('BtbRolesCheck: Usuario no tiene acceso a BTB, redirigiendo...');

    // Verificar si el usuario tiene portal 'cliente'
    const userPortal = localStorage.getItem('userPortal') || '';
    if (userPortal.includes('cliente')) {
      this.router.navigate(['/home'], { replaceUrl: true });
    } else if (userPortal.includes('backoffice')) {
      this.router.navigate(['/backoffice/dashboard'], { replaceUrl: true });
    } else {
      // Usuario no tiene ningún portal válido
      this.snackBar.open('No tienes acceso a ningún portal.', 'Close', {
        duration: 5000,
      });
      this.router.navigate(['/onboarding/login'], { replaceUrl: true });
    }
  }

  private matchesRoutePattern(url: string, routePatterns: string[]): boolean {
    return routePatterns.some(pattern => {
      if (pattern.includes(':')) {
        const regexPattern = pattern
          .replace(/:[^\/]+/g, '[^/]+')
          .replace(/\//g, '\\/');
        const regex = new RegExp(`^${regexPattern}$`);
        return regex.test(url);
      } else {
        return url === pattern;
      }
    });
  }
}
